// json-view — blocos de visualização de Input/Output/Erro compartilhados.
//
// Extraído do ActivityDetailModal (SKL-B 2026-06-12) pra reutilizar o MESMO
// padrão visual (admin: "usando mesmo padrão lá na modal de atividade") na
// modal de execução de skill e em futuras telas de telemetria:
//   - DataBlock  — objeto/JSON-string/texto-com-JSON-embutido → árvore
//                  acordeão por nível (details/summary); texto puro → <pre>
//   - Field      — label maiúsculo + botão copiar (conteúdo INTEIRO) + bloco
//   - CopyIdButton — .wf-copybtn com feedback .copied (1100ms)
//
// Classes: wf-activity.css (.wf-acpre/.wf-jt*/.wf-fld/.wf-copybtn) — importar
// o CSS na tela que usa.

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractEmbeddedJson, looksLikeMd, tryParseJson } from './json-view-utils';

/** Botão copiar do wireframe (.copybtn, feedback .copied 1100ms). */
export function CopyIdButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1100);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copiar ${label}`}
      aria-label={`Copiar ${label}`}
      className={cn('wf-copybtn', copied && 'copied')}
    >
      {copied ? <Check className="ic" aria-hidden="true" /> : <Copy className="ic" aria-hidden="true" />}
    </button>
  );
}

export function PreBlock({ value, variant }: { value: unknown; variant?: 'err' | 'danger' }) {
  let text: string;
  if (value === null || value === undefined) {
    text = '—';
  } else if (typeof value === 'string') {
    text = value;
  } else {
    try {
      text = JSON.stringify(value, null, 2);
    } catch {
      text = String(value);
    }
  }
  return (
    <pre
      className={cn(
        'wf-acpre',
        variant === 'err' && 'wf-acpre--err',
        variant === 'danger' && 'wf-acpre--danger',
      )}
    >
      {text}
    </pre>
  );
}

// AGT-08 — acordeão por nível do objeto (admin: "ao invés de mostrar o input
// inteiro, acordeons pra ir expandindo cada nível"). <details>/<summary>
// nativos: acessível por teclado e sem estado por nó. Arrays gigantes são
// truncados na renderização (o copiar pega o conteúdo INTEIRO, sem truncar).
const JSON_TREE_MAX_ITEMS = 100;

function jsonNodeSummary(value: unknown): string {
  if (Array.isArray(value)) return `[${value.length} ${value.length === 1 ? 'item' : 'itens'}]`;
  if (value !== null && typeof value === 'object') {
    const n = Object.keys(value as Record<string, unknown>).length;
    return `{${n} ${n === 1 ? 'campo' : 'campos'}}`;
  }
  return '';
}

export function JsonNode({ name, value, depth }: { name: string | null; value: unknown; depth: number }) {
  const isObj = value !== null && typeof value === 'object';
  if (!isObj) {
    // SKL-D: string multilinha/longa (MD embutido em input/output) vira BLOCO
    // com quebras preservadas; com cara de markdown → RENDERIZA (asterisco é
    // negrito, não texto cru — admin r3).
    if (typeof value === 'string' && (value.includes('\n') || value.length > 280)) {
      return (
        <div className="wf-jt-row wf-jt-row--block">
          {name !== null && <span className="wf-jt-key">{name}:</span>}
          {looksLikeMd(value) ? (
            <div className="wf-jt-strblock wf-jt-strblock--md prose prose-sm dark:prose-invert max-w-none break-words">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
            </div>
          ) : (
            <pre className="wf-jt-strblock">{value}</pre>
          )}
        </div>
      );
    }
    const display =
      value === null ? 'null' : typeof value === 'string' ? value : JSON.stringify(value);
    return (
      <div className="wf-jt-row">
        {name !== null && <span className="wf-jt-key">{name}:</span>}
        <span className={cn('wf-jt-val', typeof value === 'string' && 'wf-jt-val--str')}>
          {display}
        </span>
      </div>
    );
  }
  const entries: Array<[string, unknown]> = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as [string, unknown])
    : Object.entries(value as Record<string, unknown>);
  const shown = entries.slice(0, JSON_TREE_MAX_ITEMS);
  return (
    <details className="wf-jt-node" open={depth === 0}>
      <summary className="wf-jt-summary">
        {name !== null && <span className="wf-jt-key">{name}</span>}
        <span className="wf-jt-meta">{jsonNodeSummary(value)}</span>
      </summary>
      <div className="wf-jt-children">
        {shown.map(([k, v]) => (
          <JsonNode key={k} name={k} value={v} depth={depth + 1} />
        ))}
        {entries.length > JSON_TREE_MAX_ITEMS && (
          <div className="wf-jt-row wf-jt-more">
            … mais {entries.length - JSON_TREE_MAX_ITEMS} itens (use o copiar pra ver tudo)
          </div>
        )}
      </div>
    </details>
  );
}

/** Input/Output/Erro: objeto (ou string JSON, ou TEXTO com JSON embutido) →
 *  árvore acordeão; texto puro → <pre>. */
export function DataBlock({ value, variant }: { value: unknown; variant?: 'danger' }) {
  const parsed = tryParseJson(value);
  if (parsed !== null && typeof parsed === 'object') {
    return (
      <div className="wf-acpre wf-jt">
        <JsonNode name={null} value={parsed} depth={0} />
      </div>
    );
  }
  // AGT-09: string com JSON embutido → texto ao redor + árvore no objeto.
  if (typeof parsed === 'string') {
    const embedded = extractEmbeddedJson(parsed);
    if (embedded) {
      return (
        <div className={cn('wf-acpre wf-jt', variant === 'danger' && 'wf-acpre--danger')}>
          {embedded.before && <pre className="wf-jt-text">{embedded.before}</pre>}
          <JsonNode name={null} value={embedded.obj} depth={0} />
          {embedded.after && <pre className="wf-jt-text">{embedded.after}</pre>}
        </div>
      );
    }
  }
  return <PreBlock value={value ?? null} variant={variant} />;
}

export function Field({
  label,
  copy,
  children,
}: {
  label: string;
  /** AGT-08: conteúdo bruto completo pro botão copiar ao lado do label (fora da rolagem do bloco). */
  copy?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="wf-fld">
      <div className="wf-fk" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}
        {copy ? <CopyIdButton value={copy} label={`${label} completo`} /> : null}
      </div>
      {children}
    </div>
  );
}
